import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Image, Upload, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

const RendersEditor = ({ project, onChange }) => {
  const renders = project.renders || [];
  const vicinityMap = project.vicinity_map || '';
  const aerialView = project.aerial_view || '';
  
  const [uploading, setUploading] = useState(false);
  
  const handleImageUpload = async (e, type) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploading(true);
    
    try {
      // Convert files to base64
      const promises = Array.from(files).map(file => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (event) => {
            resolve({
              name: file.name,
              data: event.target?.result,
              type: file.type
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      });
      
      const images = await Promise.all(promises);
      
      if (type === 'renders') {
        onChange({
          ...project,
          renders: [...renders, ...images]
        });
        toast.success(`${images.length} render(s) uploaded`);
      } else if (type === 'vicinity') {
        onChange({ ...project, vicinity_map: images[0].data });
        toast.success('Vicinity map uploaded');
      } else if (type === 'aerial') {
        onChange({ ...project, aerial_view: images[0].data });
        toast.success('Aerial view uploaded');
      }
    } catch (error) {
      toast.error('Error uploading images');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };
  
  const handleRemoveRender = (index) => {
    onChange({
      ...project,
      renders: renders.filter((_, i) => i !== index)
    });
    toast.success('Render removed');
  };
  
  const handleRemoveMap = (type) => {
    if (type === 'vicinity') {
      onChange({ ...project, vicinity_map: '' });
    } else if (type === 'aerial') {
      onChange({ ...project, aerial_view: '' });
    }
    toast.success('Image removed');
  };
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
          Renders & Images
        </h2>
        <p className="text-slate-600 dark:text-slate-400">
          Upload project renders, vicinity maps, and aerial views
        </p>
      </div>
      
      {/* Project Renders */}
      <Card className="p-6 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Project Renders
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Upload multiple renders to showcase your project
            </p>
          </div>
          
          <Label
            htmlFor="renders-upload"
            className="cursor-pointer inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 h-10 px-4 py-2"
          >
            <Upload className="mr-2 h-4 w-4" />
            Upload Renders
          </Label>
          <input
            id="renders-upload"
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => handleImageUpload(e, 'renders')}
            className="hidden"
            disabled={uploading}
          />
        </div>
        
        {renders.length === 0 ? (
          <div className="p-12 text-center border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-lg">
            <Image className="mx-auto h-12 w-12 text-slate-400 mb-4" />
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              No renders uploaded yet
            </p>
            <Label
              htmlFor="renders-upload-2"
              className="cursor-pointer inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3"
            >
              <Upload className="mr-2 h-4 w-4" />
              Upload First Render
            </Label>
            <input
              id="renders-upload-2"
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => handleImageUpload(e, 'renders')}
              className="hidden"
              disabled={uploading}
            />
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {renders.map((render, index) => (
              <div key={index} className="relative group">
                <img
                  src={render.data}
                  alt={render.name}
                  className="w-full h-48 object-cover rounded-lg"
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveRender(index)}
                    className="text-white hover:text-red-400"
                  >
                    <Trash2 className="h-5 w-5" />
                  </Button>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-400 mt-2 truncate">
                  {render.name}
                </p>
              </div>
            ))}
          </div>
        )}
        
        {renders.length > 0 && (
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-xs text-slate-600 dark:text-slate-400">
              <strong>Note:</strong> The system will automatically paginate renders (4 per page) in the final documentation.
              Total pages: {Math.ceil(renders.length / 4)}
            </p>
          </div>
        )}
      </Card>
      
      {/* Vicinity Map */}
      <Card className="p-6 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Vicinity Map
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Location map showing project context
            </p>
          </div>
          
          <Label
            htmlFor="vicinity-upload"
            className="cursor-pointer inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3"
          >
            <Upload className="mr-2 h-4 w-4" />
            Upload Map
          </Label>
          <input
            id="vicinity-upload"
            type="file"
            accept="image/*"
            onChange={(e) => handleImageUpload(e, 'vicinity')}
            className="hidden"
            disabled={uploading}
          />
        </div>
        
        {vicinityMap ? (
          <div className="relative">
            <img
              src={vicinityMap}
              alt="Vicinity Map"
              className="w-full h-64 object-cover rounded-lg"
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleRemoveMap('vicinity')}
              className="absolute top-2 right-2 bg-red-600 text-white hover:bg-red-700"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="p-8 text-center border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-lg">
            <Image className="mx-auto h-8 w-8 text-slate-400 mb-2" />
            <p className="text-sm text-slate-600 dark:text-slate-400">
              No vicinity map uploaded
            </p>
          </div>
        )}
      </Card>
      
      {/* Aerial View */}
      <Card className="p-6 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Aerial View / Bird's Eye View
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Aerial photograph or satellite view of project site
            </p>
          </div>
          
          <Label
            htmlFor="aerial-upload"
            className="cursor-pointer inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3"
          >
            <Upload className="mr-2 h-4 w-4" />
            Upload Aerial View
          </Label>
          <input
            id="aerial-upload"
            type="file"
            accept="image/*"
            onChange={(e) => handleImageUpload(e, 'aerial')}
            className="hidden"
            disabled={uploading}
          />
        </div>
        
        {aerialView ? (
          <div className="relative">
            <img
              src={aerialView}
              alt="Aerial View"
              className="w-full h-64 object-cover rounded-lg"
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleRemoveMap('aerial')}
              className="absolute top-2 right-2 bg-red-600 text-white hover:bg-red-700"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="p-8 text-center border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-lg">
            <Image className="mx-auto h-8 w-8 text-slate-400 mb-2" />
            <p className="text-sm text-slate-600 dark:text-slate-400">
              No aerial view uploaded
            </p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default RendersEditor;